import {
  GET_LIST_CONFLICT,
  GET_LIST_PERIOD,
  GET_MATRIX_ACTIVE_PERIOD,
  GET_MATRIX_STATUS,
  GET_MATRIX_STRUCTURE_STATUS,
  GET_MATRIX_VIEW_PROCESS,
  GET_MATRIX_VIEW_STRUCTURE,
  GET_NEW_VIEW_MATRIX,
  GET_PIVOT_DATA,
  GET_SELECTED_MATRIX_FOCUS,
  GET_VIEW_DETAIL_OF_PROCESS,
  GET_VIEW_MATRIX,
  GET_VIEW_MATRIX_FOCUS,
  GET_VIEW_MATRIX_FOCUS_CHANGE,
} from 'redux/actions/matrix';

const reMappingProcess = (currentData, newData, isFocusMatrix) => {
  if (currentData.length === 0) return newData;

  // Only reverse if it is the Focus Matrix to build the hierarchy correctly
  const dataToProcess = isFocusMatrix ? [...newData].reverse() : newData.reverse();

  dataToProcess.forEach((item) => {
    const parentIndex = currentData.findIndex(
      (process) => process.process_id === item.parent_id,
    );
    if (parentIndex !== -1) {
      currentData.splice(parentIndex + 1, 0, item);
    }
  });
  return currentData;
};

function addEmptyArrayKey(data, key, activePeriod) {
  return data?.map((item) => {
    if (!(key in item) || !activePeriod) {
      return { ...item, [key]: [] };
    }
    return item;
  });
}

// --- SHARED INSERTION STRATEGIES ---
const STRATEGIES = {
  // Strategy for Normal Matrix
  NORMAL: (structure, newEntry, reportToParent) => {
    const indexPosition = structure.findIndex((obj) => obj.organization_id === reportToParent);
    indexPosition !== -1 ? structure.splice(indexPosition + 1, 0, newEntry) : structure.push(newEntry);
  },

  // Strategy for Focus Matrix (Maintains hierarchy order)
  FOCUS: (structure, newEntry) => {
    console.log({ structure, newEntry }, '<<debug-before');

    const parentIndex = structure.findIndex((obj) => obj.organization_id === newEntry.report_to);
    if (parentIndex !== -1) {
      let insertAfterIndex = parentIndex;
      // Scan forward to keep siblings together
      for (let i = parentIndex + 1; i < structure.length; i++) {
        if (structure[i].job_level > newEntry.job_level) insertAfterIndex = i;
        else break;
      }
      structure.splice(insertAfterIndex + 1, 0, newEntry);
    } else {
      const lastSiblingIndex = structure.findLastIndex((obj) => obj.report_to === newEntry.report_to);
      lastSiblingIndex !== -1 ? structure.splice(lastSiblingIndex + 1, 0, newEntry) : structure.push(newEntry);
    }
    console.log({ structure, newEntry }, '<<debug-after');
  },
  STRICT: (structure, newEntry, reportToParent, indexOffset = 0) => {
    // 1. Find the parent
    const parentIndex = structure.findIndex((obj) => obj.organization_id === reportToParent);

    // 2. Insert using the offset to preserve order
    if (parentIndex !== -1) {
      // Use offset to push subsequent items to the next index
      structure.splice(parentIndex + 1 + indexOffset, 0, newEntry);
    } else {
      structure.push(newEntry);
    }
  },

};

// --- THE ENGINE ---
function processMatrixUpdate(existingData, newData, isGetAllOrg, strategyCallback, reportToParent) {
  // Iterate through each process
  existingData.forEach((process) => {
    const { structure } = process;
    let insertionCount = 0; // Track insertions per process to maintain order

    // Process the new data
    newData.forEach((newOrg) => {
      const targetProcess = newOrg.process?.find((p) => p.process_id === process.process_id);
      if (!targetProcess) return;

      const existingOrg = structure.find((item) => item.organization_id === newOrg.organization_id);

      if (existingOrg) {
        // Update in-place
        Object.assign(existingOrg, {
          value: targetProcess.value,
          name: newOrg.name,
          job_level_name: newOrg.name,
          order_number: newOrg.order_number,
          job_level: newOrg.job_level,
          count_child: newOrg.count_child,
          report_to: newOrg.report_to,
          process_id: process.process_id,
        });
      } else {
        if (isGetAllOrg && structure.length === newData.length) return;

        const newEntry = {
          ...newOrg,
          job_level_name: newOrg.name,
          name: newOrg.name,
          value: targetProcess.value,
          process_id: process.process_id,
        };
        delete newEntry.process;

        // Pass the insertionCount as the indexOffset
        strategyCallback(structure, newEntry, reportToParent, insertionCount);

        // Increment the offset so the next item lands after this one
        insertionCount++;
      }
    });
  });
  return existingData;
}

function insertStructure(existingData, newData, isGetAllOrg, reportToParent, isFocusMatrix) {
  const processedData = isFocusMatrix ? [...newData].reverse() : newData.reverse();

  // Apply conditional reversals
  const lastItem = processedData[processedData.length - 1];
  if ((!isFocusMatrix && processedData.length > 1 && lastItem?.job_level === 1)
      || (isFocusMatrix && lastItem?.job_level < processedData[0]?.job_level)) {
    processedData.reverse();
  }

  const result = processMatrixUpdate(existingData, processedData, isGetAllOrg, STRATEGIES.NORMAL, reportToParent);

  // Post-process validation: Ensure root is at the end
  result.forEach((p) => {
    if (p.structure.length > 1 && p.structure[p.structure.length - 1].job_level === 1 && p.structure[0].job_level !== 1) {
      p.structure.reverse();
    }
  });
  return result;
}

function insertStructureFocus(existingData, newData, isGetAllOrg, reportToParent, isFocusMatrix) {
  // Directly copy the array to avoid mutating the original payload
  // Removing .reverse() ensures the order is preserved exactly as passed.
  console.log('<<ndt', { ...newData });

  const processedData = [...newData];

  // We pass this directly to the engine, which will now iterate
  // through the array in the order you provided.
  return processMatrixUpdate(existingData, processedData, isGetAllOrg, STRATEGIES.STRICT, reportToParent);
}
// function insertStructure(
//   existingData,
//   newData,
//   isGetAllOrg,
//   reportToParent,
//   isFocusMatrix
// ) {
//   newData.forEach(newOrg => {
//     const newOrgId = newOrg.organization_id;
//     const reportToId = newOrg.report_to;

//     existingData.forEach(process => {
//       const { structure } = process;

//       // 1. Extract the specific value for this process from the source
//       const targetProcess = newOrg.process.find(p => p.process_id === process.process_id);
//       if (!targetProcess) return;

//       const newValue = targetProcess.value;

//       // 2. Check if this organization already exists in the structure
//       const existingOrg = structure.find(item => item.organization_id === newOrgId);

//       if (existingOrg) {
//         /**
//          * UPDATE IN-PLACE
//          * We modify the object properties directly.
//          * This ensures the index/position in the array never changes.
//          */
//         existingOrg.value = newValue; // Correctly sets 'mr' to null
//         existingOrg.name = newOrg.name;
//         existingOrg.job_level_name = newOrg.name;
//         existingOrg.order_number = newOrg.order_number;
//         existingOrg.job_level = newOrg.job_level;
//         existingOrg.count_child = newOrg.count_child;
//         existingOrg.report_to = newOrg.report_to;

//         // Ensure the process_id is synced
//         existingOrg.process_id = process.process_id;

//       } else {
//         /**
//          * INSERT LOGIC (Only runs if the ID is totally new)
//          */
//         if (isGetAllOrg && structure.length === newData.length) return;

//         const newEntry = {
//           ...newOrg,
//           organization_id: newOrgId,
//           job_level_name: newOrg.name,
//           name: newOrg.name,
//           value: newValue,
//           process_id: process.process_id
//         };
//         delete newEntry.process;

//         if (structure.length === 0) {
//           structure.push(newEntry);
//         } else {
//           // Find insertion point based on reportToParent or reportToId
//           const indexPosition = structure.findIndex(
//             obj => obj.organization_id === (reportToParent || reportToId)
//           );

//           if (indexPosition !== -1) {
//             structure.splice(indexPosition + 1, 0, newEntry);
//           } else {
//             structure.push(newEntry);
//           }
//         }
//       }
//     });
//   });
//   return existingData;
// }

const initialState = {
  matrix: [],
  real_matrix: [],
  matrix_focus: [],
  selected_matrix_focus: '',
  list_period: [],
  structure: [],
  conflict: [],
  pivot: [],
  dnd_matrix: [],
  matrix_status: undefined,
  active_period: '',
  detail_of_process: [],
};
// eslint-disable-next-line import/no-anonymous-default-export

// eslint-disable-next-line import/no-anonymous-default-export
export default (state = initialState, { type, payload }) => {
  switch (type) {
    case GET_NEW_VIEW_MATRIX: {
      return {
        ...state,
        dnd_matrix: payload,
      };
    }
    case GET_MATRIX_STATUS: {
      return {
        ...state,
        matrix_status: payload,
      };
    }
    case GET_VIEW_MATRIX: {
      let matrixDefault = [...state.matrix];

      // If payload contains a specific value change (A, R, MR)
      if (payload.value) {
        const temp = [...state.matrix];
        temp.some((v, i) => {
          if (v.process_id === payload.process_id) {
            // Update R/A/MR counts
            if (['a', 'mr', 'r'].includes(payload.prevValue)) {
              if (payload.prevValue === 'r') temp[i].r_count = v.r_count - 1;
              if (payload.prevValue === 'a') temp[i].a_count = v.a_count - 1;
              if (payload.prevValue === 'mr') temp[i].mr_count = v.mr_count - 1;
            }
            if (['a', 'mr', 'r'].includes(payload.value)) {
              if (payload.value === 'r') temp[i].r_count = v.r_count + 1;
              if (payload.value === 'a') temp[i].a_count = v.a_count + 1;
              if (payload.value === 'mr') temp[i].mr_count = v.mr_count + 1;
            }
            // Update specific organization value
            v.structure.forEach((vz, iz) => {
              if (vz.organization_id === payload.org_id) {
                temp[i].structure[iz].value = payload.value;
              }
            });
          }
        });

        return {
          ...state,
          matrix: [...temp],
        };
      }

      // If payload is a new structure insertion
      if (matrixDefault?.length > 0) {
        // Find the index where the new structure should be inserted
        const insertIndex = matrixDefault[0].structure
          .map((object) => object.organization_id)
          .indexOf(payload[0].structure[0].organization_id);

        matrixDefault.forEach((val, i) => {
          const incoming = payload[i].structure;

          // Insert in-place without reversing (since isFocusMatrix = false)
          matrixDefault[i].structure.splice(insertIndex === -1 ? matrixDefault[i].structure.length : insertIndex, 0, ...incoming);

          // Remove duplicates while maintaining order
          const uniqueStructure = [
            ...new Map(
              matrixDefault[i].structure.map((item) => [item.organization_id, item]),
            ).values(),
          ];
          matrixDefault[i].structure = uniqueStructure;
        });
      } else {
        matrixDefault = [...payload];
      }

      return {
        ...state,
        matrix: [...matrixDefault],
      };
    }
    case GET_VIEW_MATRIX_FOCUS_CHANGE: {
      let matrixDefault = [...state.matrix_focus];
      if (payload.value) {
        const temp = [...state.matrix_focus];
        temp.some((v, i) => {
          if (v.process_id === payload.process_id) {
            // Update counts (R, A, MR)
            if (['a', 'mr', 'r'].includes(payload.prevValue)) {
              if (payload.prevValue === 'r') temp[i].r_count = (v.r_count || 0) - 1;
              if (payload.prevValue === 'a') temp[i].a_count = (v.a_count || 0) - 1;
              if (payload.prevValue === 'mr') temp[i].mr_count = (v.mr_count || 0) - 1;
            }
            if (['a', 'mr', 'r'].includes(payload.value)) {
              if (payload.value === 'r') temp[i].r_count = (v.r_count || 0) + 1;
              if (payload.value === 'a') temp[i].a_count = (v.a_count || 0) + 1;
              if (payload.value === 'mr') temp[i].mr_count = (v.mr_count || 0) + 1;
            }
            // Update value in structure
            v.structure.forEach((vz, iz) => {
              if (vz.organization_id === payload.org_id) {
                temp[i].structure[iz].value = payload.value;
              }
            });
          }
        });

        return {
          ...state,
          matrix_focus: [...temp],
        };
      }

      // Standard structure update for focus matrix
      if (matrixDefault?.length > 0) {
        const insertIndex = matrixDefault[0].structure
          .map((object) => object.organization_id)
          .indexOf(payload[0].structure[0].organization_id);

        matrixDefault.forEach((val, i) => {
          const incoming = payload[i].structure;
          // In focus matrix, we handle insertion order via the insertStructure helper usually,
          // but if using this case, we maintain the index-based splice.
          matrixDefault[i].structure.splice(insertIndex === -1 ? matrixDefault[i].structure.length : insertIndex, 0, ...incoming);

          const uniqueStructure = [
            ...new Map(
              matrixDefault[i].structure.map((item) => [item.organization_id, item]),
            ).values(),
          ];
          matrixDefault[i].structure = uniqueStructure;
        });
      } else {
        matrixDefault = [...payload];
      }

      return {
        ...state,
        matrix_focus: [...matrixDefault],
      };
    }

    case GET_LIST_PERIOD: {
      return {
        ...state,
        list_period: payload,
      };
    }
    case GET_MATRIX_STRUCTURE_STATUS: {
      return {
        ...state,
        structure: payload,
      };
    }
    case GET_LIST_CONFLICT: {
      return {
        ...state,
        conflict: payload,
      };
    }
    case GET_PIVOT_DATA: {
      return {
        ...state,
        pivot: payload,
      };
    }
    // REVAMP v2
    case GET_MATRIX_VIEW_PROCESS: {
      const isSamePeriod = payload.body.periode_matrix_id === state.active_period;
      const tempData = reMappingProcess(
        !isSamePeriod ? [] : state.matrix,
        payload.data,
      );
      const newMatrix = addEmptyArrayKey(
        tempData,
        'structure',
        payload.body.periode_matrix_id === state.active_period,
      );
      // if (payload?.body?.initialState) {
      //   state.matrix_focus = [...newMatrix];
      // }
      return {
        ...state,
        matrix: [...newMatrix],
      };
    }
    case GET_VIEW_MATRIX_FOCUS: {
      const isSamePeriod = payload.body?.periode_matrix_id === state.active_period;
      const tempData = reMappingProcess(
        !isSamePeriod ? [] : state.matrix_focus,
        payload.data,
      );

      const newMatrix = addEmptyArrayKey(
        tempData,
        'structure',
        payload.body.periode_matrix_id === state.active_period,
      );
      return {
        ...state,
        matrix_focus: [...newMatrix],
      };
    }
    case GET_MATRIX_ACTIVE_PERIOD: {
      return {
        ...state,
        active_period: payload,
      };
    }
    case GET_MATRIX_VIEW_STRUCTURE: {
      const isFocus = !!payload.focus; // Ensure boolean
      const keyMatrix = isFocus ? 'matrix_focus' : 'matrix';

      let selectMatrix = isFocus ? [...state.matrix_focus] : [...state.matrix];
      if ((isFocus && selectMatrix.length === 0) || (selectMatrix.length !== state.matrix.length)) {
        selectMatrix = addEmptyArrayKey(state.matrix, 'structure', false);
      }
      if (selectMatrix.length === 0) {
        return state;
      }
      const newMatrix = isFocus
        ? insertStructureFocus(selectMatrix, payload.data, payload?.isGetAllOrg, payload?.report_to, isFocus)
        : insertStructure(selectMatrix, payload.data, payload?.isGetAllOrg, payload?.report_to, isFocus);
      console.log(newMatrix, '<<ddd-c');

      return {
        ...state,
        matrix_status: true,
        [keyMatrix]: [...newMatrix],
      };
    }

    case GET_SELECTED_MATRIX_FOCUS: {
      return {
        ...state,
        selected_matrix_focus: payload,
      };
    }

    case GET_VIEW_DETAIL_OF_PROCESS: {
      return {
        ...state,
        detail_of_process: payload,
      };
    }

    default:
      return state;
  }
};
